import { useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { MaintCardShell } from './MaintCardShell';
import { ConfirmApplyDialog } from './ConfirmApplyDialog';

export default function CommissionsCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [applying, setApplying] = useState(false);

  const scan = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/maintenance/commissions/scan');
      setData(res.data);
      setSelected({});
    } catch (e) { toast.error(e.response?.data?.detail || 'Scan failed'); }
    finally { setLoading(false); }
  };

  const selectedCount = Object.values(selected).filter(Boolean).length;

  const apply = async () => {
    setApplying(true);
    try {
      const mapping = {};
      data.commissions.filter((c) => selected[c.commission_id]).forEach((c) => {
        mapping[c.commission_id] = { base_amount: c.proposed_base_amount, commission_amount: c.proposed_commission_amount };
      });
      const res = await api.post('/admin/maintenance/commissions/apply', { mapping });
      toast.success(`${res.data.total_updated} commission(s) recalculated`);
      setConfirmOpen(false);
      scan();
    } catch (e) { toast.error(e.response?.data?.detail || 'Apply failed'); }
    finally { setApplying(false); }
  };

  return (
    <MaintCardShell
      title="Commissions to recalculate"
      description="Pending/approved commissions (never paid ones) recalculated against the job's CURRENT total, after job discounts were corrected."
      testId="commissions"
      count={data ? data.total_affected : null}
      loading={loading}
      onScan={scan}
    >
      {data && data.commissions.length > 0 && (
        <div className="space-y-3">
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {data.commissions.map((c) => (
              <div key={c.commission_id} className="border border-gray-100 rounded-lg p-3 text-xs" data-testid={`commissions-row-${c.commission_id}`}>
                <label className="flex items-start gap-2 cursor-pointer">
                  <Checkbox data-testid={`commissions-row-${c.commission_id}-checkbox`} checked={!!selected[c.commission_id]} onCheckedChange={(v) => setSelected((s) => ({ ...s, [c.commission_id]: !!v }))} />
                  <div>
                    <p className="font-semibold text-gray-800">{c.user_name} · {c.job_number} · {c.status}</p>
                    <p className="text-gray-600">${c.current_commission_amount} → ${c.proposed_commission_amount} (base ${c.current_base_amount} → ${c.proposed_base_amount})</p>
                  </div>
                </label>
              </div>
            ))}
          </div>
          <Button data-testid="commissions-apply-btn" size="sm" disabled={selectedCount === 0} onClick={() => setConfirmOpen(true)} className="bg-red-600 hover:bg-red-700 text-white">
            Apply selected ({selectedCount})
          </Button>
        </div>
      )}
      {data && data.commissions.length === 0 && <p className="text-xs text-emerald-600">Nothing to recalculate.</p>}

      <ConfirmApplyDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={apply}
        confirming={applying}
        title="Recalculate commissions"
        summary={`This will update base/commission amounts on ${selectedCount} selected commission(s). Status (pending/approved/paid) is never changed.`}
      />
    </MaintCardShell>
  );
}
